// src/hooks/useAuth.js - Mit Development Mock für localhost:3000
// Team table based access control - only registered team members can access the app
import { useState, useEffect, useCallback } from "react";
import { getTeamMemberByEmail } from "../services/azureTeamService";

const getMockUser = () => {
  const email = import.meta.env.VITE_DEV_USER_EMAIL || "dev@localhost.com";
  const name = import.meta.env.VITE_DEV_USER_NAME || "Development User";
  // Supported roles: MF-Admin, MF-PL, MF-Team, MF-Freelancer
  const role = import.meta.env.VITE_DEV_USER_ROLE || "MF-Admin";
  // For freelancers, we need to store assigned project codes
  const assignedProjects = import.meta.env.VITE_DEV_USER_PROJECTS
    ? import.meta.env.VITE_DEV_USER_PROJECTS.split(",").map(p => p.trim())
    : [];

  return {
    id: "dev-user-1",
    name: name,
    email: email,
    provider: "mock",
    roles: [role],
    userDetails: email,
    userRoles: [role],
    assignedProjects: assignedProjects, // For freelancers: project codes they can access
  };
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [deniedUserEmail, setDeniedUserEmail] = useState(null);

  // Development Mode Detection
  const isDevelopment =
    import.meta.env.DEV &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1") &&
    (window.location.port === "3000" || window.location.port === "3001");

  // Validate user against Team table and auto-assign roles
  const validateAndEnhanceUser = async (baseUser) => {
    try {
      const userEmail = baseUser.email || baseUser.userDetails;

      const teamMember = await getTeamMemberByEmail(userEmail);

      if (!teamMember) {
        console.warn("useAuth: User not found in Team table:", userEmail);
        setAccessDenied(true);
        setDeniedUserEmail(userEmail);
        setUser(null);
        return null;
      }

      // Enhance user object with Team table data
      const enhancedUser = {
        ...baseUser,
        // Override roles with Team table role
        roles: [teamMember.assignedRole],
        userRoles: [teamMember.assignedRole],
        // Add team member data
        teamMember: teamMember,
        kategorie: teamMember.kategorie,
        isFreelancer: teamMember.isFreelancer,
        noAbaNinja: teamMember.noAbaNinja || false,
        assignedProjects: teamMember.zugeordneteProjekte || [],
      };

      setAccessDenied(false);
      setDeniedUserEmail(null);
      return enhancedUser;
    } catch (err) {
      console.error("Team table validation failed:", err);
      // On error, deny access for safety
      setAccessDenied(true);
      setDeniedUserEmail(baseUser.email || baseUser.userDetails);
      return null;
    }
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Development Mock
      if (isDevelopment) {
        const mockUser = getMockUser();

        // In dev mode, validate against Team table too (can be bypassed with VITE_DEV_SKIP_TEAM_CHECK=true)
        const skipTeamCheck = import.meta.env.VITE_DEV_SKIP_TEAM_CHECK === "true";
        if (skipTeamCheck) {
          setUser(mockUser);
        } else {
          const enhancedUser = await validateAndEnhanceUser(mockUser);
          setUser(enhancedUser);
        }
        setLoading(false);
        return;
      }

      // Production/SWA CLI Authentication
      const response = await fetch("/.auth/me", {
        cache: "no-cache",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();

        if (data?.clientPrincipal) {
          // Build base user from Azure AD
          const baseUser = {
            id: data.clientPrincipal.userId,
            name: data.clientPrincipal.userDetails,
            email: data.clientPrincipal.userDetails,
            provider: data.clientPrincipal.identityProvider,
            userDetails: data.clientPrincipal.userDetails,
          };

          // Validate against Team table and get role
          const enhancedUser = await validateAndEnhanceUser(baseUser);
          setUser(enhancedUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setError(err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [isDevelopment]);

  const login = useCallback(() => {
    if (isDevelopment) {
      checkAuthStatus();
      return;
    }
    window.location.href = "/.auth/login/aad";
  }, [isDevelopment, checkAuthStatus]);

  const logout = useCallback(() => {
    if (isDevelopment) {
      setUser(null);
      return;
    }
    window.location.href = "/.auth/logout";
  }, [isDevelopment]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  return {
    user,
    loading,
    error,
    login,
    logout,
    refresh: checkAuthStatus,
    isAuthenticated: !!user,
    hasRole: (role) => user?.userRoles?.includes(role) || false,
    isDevelopmentMode: isDevelopment, // Zusätzliche Info für Debugging
    // Team table access control
    accessDenied,
    deniedUserEmail,
  };
};
